// here is the place where import new modules
// note! sequence is important!
import './auth';
import './cancellation';
import './chat';
import './legal';
import './settings';
// landing page removed — chat is now the root route
import api, { REST_API_BASE_URL_LOCALSTORAGE_KEY } from '~/Api';
import { USER_ID_KEY } from '~/constants';
import { getRootDomain } from '~/helpers/getRootDomain';
import i18n from '~/i18n/config';
import projectConfig from '~/projects/config';
import { growthBook } from '~/services/abTests/growthBook';
import analytics from '~/services/analytics';
import errorLogger from '~/services/ErrorLogger';
import QueryParams from '~/services/QueryParams';
import sessionCounter from '~/services/sessionCounter';
import localStorage from '~/services/storage/localStorage';
import sessionStorage from '~/services/storage/sessionStorage';
import userFieldsService from '~/services/UserFields';
import versionUpdater from '~/services/VersionUpdater';
import { getSource, saveSource } from '~/utils/getSource';
import parseUTM from '~/utils/parseUTM';
import { getUserBrowser } from '~/utils/userAgentInfo';

const SITE_BASE_URL = process.env.SITE_BASE_URL || '';
const IS_DEVELOPMENT = process.env.NODE_ENV === 'development';

let userId = QueryParams.getQueryParam('user_id');
const langFromQueryParams = QueryParams.getQueryParam('lang');
const source = getSource();
saveSource(source);

if (userId) {
  localStorage.setByKey(USER_ID_KEY, userId);
} else {
  userId = localStorage.getByKey(USER_ID_KEY);
}

// init global analytic
analytics.init({
  userId: userId,
  eventParams: {
    browser: getUserBrowser(),
  },
  userProperties: {
    project: projectConfig.project,
  },
});

analytics.addDefaultEventParams({ source });

const SENTRY_DSN = process.env.SENTRY_DSN;

if (SENTRY_DSN) {
  errorLogger.init({ userId, sentryDsn: SENTRY_DSN });
}

const GITHUB_SHA = process.env.GITHUB_SHA;

if (GITHUB_SHA && !IS_DEVELOPMENT) {
  versionUpdater.init({ currentVersion: GITHUB_SHA });
}

sessionCounter.init();
userFieldsService.init();

const GROWTH_BOOK_API_KEY = process.env.GROWTH_BOOK_API_KEY;

if (GROWTH_BOOK_API_KEY) {
  growthBook.setUp(GROWTH_BOOK_API_KEY, [
    (experiment, result): void => {
      analytics.setUserProperties({
        [experiment.key]: result.value,
      });
      analytics.trackEvent('experiment - started', {
        ...result,
        ...experiment,
      });
    },
  ]);
}

api.subscribeUpdateTokens((tokens) => {
  try {
    if (!SITE_BASE_URL) {
      return;
    }
    const COOKIE_NAME = `${new URL(SITE_BASE_URL).host}_logged_in`;
    if (tokens) {
      const payload = tokens.accessToken.split('.')[1];
      const { iat } = JSON.parse(atob(payload));
      if (!iat) {
        return;
      }
      const expires = new Date(iat * 1000 + 30 * 864e5).toUTCString();
      document.cookie = `${COOKIE_NAME}=true; expires=${expires}; path=/; domain=.${getRootDomain()}; secure; samesite=lax`;
    } else {
      document.cookie = `${COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${getRootDomain()}`;
    }
  } catch (e) {
    console.error(e);
  }
});

i18n.init((detectedLang, platformLang) => {
  analytics.setUserProperties({
    browser_lang: navigator.language,
    detected_lang: detectedLang,
    platform_lang: platformLang,
  });
});

if (langFromQueryParams) {
  analytics.setUserProperties({
    funnel_lang: langFromQueryParams,
  });
}

const utmUserProperties = parseUTM();
if (utmUserProperties) {
  const initialUrl = window.location.pathname + window.location.search;
  userFieldsService.updateUrl(initialUrl);
  analytics.setUserProperties(utmUserProperties);
  analytics.trackEvent('utm_tags_update', {
    ...utmUserProperties,
    raw_link: initialUrl,
  });
}

const utmContent = QueryParams.getQueryParam('utm_content');
if (source === 'email' && utmContent) {
  api.trackEmailClick({ user_id: utmContent });
}

declare global {
  interface Window {
    setRestApiBaseUrl: (baseUrl: string) => void;
  }
}

window.setRestApiBaseUrl = (baseUrl: string): void => {
  localStorage.setByKey(REST_API_BASE_URL_LOCALSTORAGE_KEY, baseUrl);
  window.location.reload();
};

if (!sessionStorage.getByKey('is_app_initialized')) {
  const analyticsData = {
    page: new URL(window.location.href).pathname,
    source,
  };

  analytics.trackEvent('application - initialized', analyticsData);
  sessionStorage.setByKey('is_app_initialized', 'true');
}
